import { useState } from 'react'
import { ApiRequestError } from '../../../lib/api'
import type { ReviewQuestion } from '../../../stores/onboardingV2Store'
import { INPUT_CLASS } from './formStyles'

interface Props {
  question: ReviewQuestion
  onAnswer: (value: string, customValue?: string) => Promise<void>
}

// Type M (metadata contradiction): chip-choice, one-shot. Selection is the
// action — no Save button. "Let me type the correct value" is always available.
export default function ReviewMetadataQuestion({ question, onAnswer }: Props) {
  const [typing, setTyping] = useState(false)
  const [customValue, setCustomValue] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const pick = async (value: string, cv?: string) => {
    setBusy(true)
    setError(null)
    try {
      await onAnswer(value, cv)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.error.message : 'Something went wrong — try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="border border-ob-accent/40 bg-ob-accent/5 p-4">
      <p className="mb-2 flex items-center gap-1.5 font-label text-[11px] uppercase tracking-wider text-ob-accent">
        <span className="material-symbols-outlined text-[16px]" aria-hidden="true">help</span>
        Quick question
      </p>
      <p className="mb-3 font-body text-sm text-ob-on-surface">{question.text}</p>
      <div className="flex flex-col gap-2">
        {(question.options ?? []).map((opt) => (
          <button
            key={opt.value}
            type="button"
            disabled={busy}
            onClick={() => void pick(opt.value)}
            className="flex min-h-[44px] flex-col justify-center border border-ob-ink px-4 py-2 text-start font-body text-sm text-ob-ink transition-colors hover:bg-ob-ink/5 disabled:opacity-50"
          >
            <span>{opt.label}</span>
            {opt.source_label && (
              <span className="font-body text-xs text-ob-secondary">from {opt.source_label}</span>
            )}
          </button>
        ))}
        {!typing ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => setTyping(true)}
            className="min-h-[44px] px-1 text-start font-body text-sm text-ob-secondary underline transition-colors hover:text-ob-ink disabled:opacity-50"
          >
            Let me type the correct value
          </button>
        ) : (
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              autoFocus
              placeholder="Type the correct value"
              className={INPUT_CLASS}
            />
            <button
              type="button"
              disabled={busy || !customValue.trim()}
              onClick={() => void pick('__custom__', customValue.trim())}
              className="min-h-[44px] shrink-0 border border-ob-ink px-4 font-body text-sm font-semibold text-ob-ink transition-colors hover:bg-ob-ink/5 disabled:opacity-50"
            >
              Use this
            </button>
          </div>
        )}
      </div>
      {error && (
        <p className="mt-3 font-body text-sm text-ob-error" role="alert">{error}</p>
      )}
    </div>
  )
}
